import React, { useMemo, useState, useCallback } from 'react'
import { useQuery, useMutation } from '@apollo/client'
import { Container } from '@mui/material'
import { useSelector } from 'react-redux'
import { selectAuth } from 'services/auth/redux/selectors'
import { getAuthPermissions, PERM_UPDATE_CONTENT } from 'services/auth'
import { getIdFromLocation } from 'services/content'
import { selectLocation } from 'services/location/redux/selectors'
import SectionAccordion from 'gui/components/accordion/section-accordion'
import ContentDetailQueryStates from './ContentDetailQueryStates'
import ModulesSection from './course-modules-section'
import CourseContentInformationSection from './CourseContentInformationSection'
import CourseContentReferencesSection from './CourseContentReferencesSection'
import CourseContentReferencesExtendedSection from './CourseContentReferencesExtendedSection'
import { GET_COURSE_CONTENT, UPDATE_COURSE_CONTENT } from './queries'
import { useSectionManagedSlot } from './useSectionManagedSlot'
import ContentMediaSection from './content-detail-media/content-media-section'
import ContentImagesSection from './content-detail-images/content-images-section'
import FloatingSaveCancelButtons from './FloatingSaveCancelButtons'
import ContentDetailSnackbar, { useContentDetailSnackbar } from './ContentDetailSnackbar'
import type { ContentInfoChanges, CourseModuleReference } from './types'
import './course-detail.scss'

/**
 * Course Detail Component
 * Displays and allows editing of Course content with Modules
 *
 * This component uses Apollo Client for data fetching (modern YI pattern)
 * No Redux for content data - only for auth and location
 */
function CourseDetail () {
  const auth = useSelector(selectAuth)
  const location = useSelector(selectLocation)
  const authPermissions = getAuthPermissions(auth)
  const isViewOnlyMode = !authPermissions.includes(PERM_UPDATE_CONTENT)

  // Get content ID from URL
  const id = getIdFromLocation(location)

  // Fetch course data from GraphQL
  const { loading, error, data, refetch } = useQuery(GET_COURSE_CONTENT, {
    variables: { id },
    skip: !id,
  })

  // Get data from GraphQL response as plain object
  const courseData = useMemo(() => data?.contentList?.content?.[0] || null, [data])

  // Extract course modules from the data
  const modules = useMemo<CourseModuleReference[]>(
    () => courseData?.contentReferencesTo || [],
    [courseData],
  )

  // Parent-managed: sections report changes up, parent owns mutation
  const [contentInfoDirty, setContentInfoDirty] = useState<boolean>(false)
  const [contentInfoChanges, setContentInfoChanges] = useState<ContentInfoChanges | null>(null)
  const [mediaDirty, setMediaDirty] = useState<boolean>(false)
  const [mediaChanges, setMediaChanges] = useState<ContentInfoChanges | null>(null)

  // Section-managed: sections own save, expose handler (content refs use ref to avoid "update child while rendering parent")
  const contentRefs = useSectionManagedSlot()
  const contentRefsExtended = useSectionManagedSlot()
  const [modulesDirty, setModulesDirty] = useState<boolean>(false)
  const [modulesSaveHandler, setModulesSaveHandler] = useState<(() => Promise<void>) | null>(null)

  const [resetTrigger, setResetTrigger] = useState<number>(0)
  const isDirty =
    contentInfoDirty || mediaDirty || contentRefs.dirty || contentRefsExtended.dirty || modulesDirty

  // Handle dirty change from Content Information
  const handleContentInfoDirtyChange = useCallback((isContentDirty: boolean) => {
    setContentInfoDirty(isContentDirty)
  }, [])

  // Handle changes update from Content Information
  const handleContentInfoChangesUpdate = useCallback((changes: ContentInfoChanges | null) => {
    setContentInfoChanges(changes)
  }, [])

  // Handle dirty change from Media section
  const handleMediaDirtyChange = useCallback((isMediaDirty: boolean) => {
    setMediaDirty(isMediaDirty)
  }, [])

  // Handle changes update from Media section
  const handleMediaChangesUpdate = useCallback((changes: ContentInfoChanges | null) => {
    setMediaChanges(changes)
  }, [])

  // Handle dirty change from Modules
  const handleModulesDirtyChange = useCallback((isStepsDirty: boolean) => {
    setModulesDirty(isStepsDirty)
  }, [])

  // Handle save handler ready from Modules
  const handleModulesSaveHandlerReady = useCallback((saveHandler: () => Promise<void>) => {
    setModulesSaveHandler(() => saveHandler)
  }, [])

  // Mutation for updating course content
  const [updateCourseContent, { loading: saving }] = useMutation(UPDATE_COURSE_CONTENT, {
    refetchQueries: [{ query: GET_COURSE_CONTENT, variables: { id } }],
    awaitRefetchQueries: true,
  })

  const { snackbar, showSnackbar, handleClose: handleSnackbarClose } = useContentDetailSnackbar()

  // Handle save
  const handleSave = async () => {
    try {
      if (
        !contentInfoChanges &&
        !mediaChanges &&
        !contentRefs.dirty &&
        !contentRefsExtended.dirty &&
        !modulesDirty
      ) {
        showSnackbar('No changes to save', 'info')
        return
      }

      let combinedChanges: ContentInfoChanges | null = null
      if (contentInfoChanges && mediaChanges) {
        combinedChanges = {
          contentInput: {
            ...contentInfoChanges.contentInput,
            ...mediaChanges.contentInput,
          },
          generatePath: contentInfoChanges.generatePath || mediaChanges.generatePath || false,
        }
      } else if (contentInfoChanges) {
        combinedChanges = contentInfoChanges
      } else if (mediaChanges) {
        combinedChanges = mediaChanges
      }

      if (combinedChanges) {
        await updateCourseContent({
          variables: {
            content: combinedChanges.contentInput,
            generatePath: combinedChanges.generatePath,
          },
        })
      }

      if (contentRefs.dirty && contentRefs.saveHandlerRef.current) {
        await contentRefs.saveHandlerRef.current()
      }
      if (contentRefsExtended.dirty && contentRefsExtended.saveHandlerRef.current) {
        await contentRefsExtended.saveHandlerRef.current()
      }

      if (modulesDirty && modulesSaveHandler) {
        await modulesSaveHandler()
      }

      await refetch()

      showSnackbar('Course saved successfully!', 'success')
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error'
      showSnackbar(`Error saving: ${errorMessage}`, 'error')
    }
  }

  const handleCancel = () => {
    setResetTrigger((prev) => prev + 1)
    showSnackbar('Changes discarded', 'info')
  }

  return (
    <ContentDetailQueryStates
      loading={loading}
      error={error}
      hasData={!!courseData}
      errorTitle="Error loading Course"
      notFoundTitle="Course not found"
      notFoundDetail={`No Course found with ID: ${id}`}
    >
      <Container className="course-detail content-detail" maxWidth={false}>
        {/* Content Information Section */}
        <Container className="content-detail__section content-information" maxWidth={false}>
          <CourseContentInformationSection
            contentData={courseData}
            isViewOnly={isViewOnlyMode}
            onDirtyChange={handleContentInfoDirtyChange}
            onChangesUpdate={handleContentInfoChangesUpdate}
            onResetTriggered={resetTrigger}
          />
        </Container>

        {/* Course Modules Section */}
        <Container className="content-detail__section course-modules" maxWidth={false}>
          <SectionAccordion title="Course Modules" collapsed={false}>
            <ModulesSection
              modules={modules}
              disabled={isViewOnlyMode}
              courseLanguage={courseData?.language}
              courseContentId={courseData?.contentId}
              courseContentType={courseData?.contentType}
              courseId={courseData?.id}
              onDirtyChange={handleModulesDirtyChange}
              onSaveHandlerReady={handleModulesSaveHandlerReady}
              onResetTriggered={resetTrigger}
            />
          </SectionAccordion>
        </Container>

        <CourseContentReferencesSection
          contentData={courseData}
          isViewOnly={isViewOnlyMode}
          onDirtyChange={contentRefs.onDirtyChange}
          saveHandlerRef={contentRefs.saveHandlerRef}
          onResetTriggered={resetTrigger}
        />

        <CourseContentReferencesExtendedSection
          contentData={courseData}
          isViewOnly={isViewOnlyMode}
          onDirtyChange={contentRefsExtended.onDirtyChange}
          saveHandlerRef={contentRefsExtended.saveHandlerRef}
          onResetTriggered={resetTrigger}
        />

        {!isViewOnlyMode && (
          <FloatingSaveCancelButtons
            visible={isDirty}
            onSave={handleSave}
            onCancel={handleCancel}
            saving={saving}
          />
        )}

        <ContentDetailSnackbar
          open={snackbar.open}
          message={snackbar.message}
          severity={snackbar.severity}
          onClose={handleSnackbarClose}
        />

        {/* Future sections can go here */}
        {/* TODO: Add Content Policy section */}

        <Container className="content-detail__section media-information" maxWidth={false}>
          <SectionAccordion title="Media Information" collapsed={false}>
            <ContentMediaSection
              contentData={courseData}
              isViewOnly={isViewOnlyMode}
              onDirtyChange={handleMediaDirtyChange}
              onChangesUpdate={handleMediaChangesUpdate}
              onResetTriggered={resetTrigger}
            />
          </SectionAccordion>
        </Container>

        <Container className="content-detail__section content-images" maxWidth={false}>
          <SectionAccordion title="Images" collapsed={false}>
            <ContentImagesSection
              contentId={courseData?.contentId}
              contentType={courseData?.contentType}
              language={courseData?.language}
              primaryLanguage={courseData?.primaryLanguage}
            />
          </SectionAccordion>
        </Container>
      </Container>
    </ContentDetailQueryStates>
  )
}

export default CourseDetail
